import { useEffect, useState } from 'react'

import { companyWasteTranslationKeyById } from '@/lib/company-waste'

import { ProductCard } from './product-card'
import { ProductVariantsList } from './product-variants-list'
import { useTranslations } from 'next-intl'

const companyWasteBagItems = [
  { path: 'tralle', label: 'Size_tralle', volume: '160L', imageSrc: '/waste-bag-size/tralle_isekk.svg', variants: [] },
  {
    path: 'standard',
    label: 'Size_standard',
    volume: '800L',
    imageSrc: '/waste-bag-size/standard_isekk.svg',
    variants: [],
  },
  { path: 'stor', label: 'Size_stor', volume: '2200L', imageSrc: '/waste-bag-size/large_isekk.svg', variants: [] },
  { path: 'xxl', label: 'Size_xxl', volume: '2400L', imageSrc: '/waste-bag-size/XXL_isekk.svg', variants: [] },
]

const companyStorXxlAllowedWasteIds = [
  '119932',
  '119939',
  '114904',
  '70980036',
  '161502',
  '113102',
  '161702',
  '149946',
  '122111',
  '159905',
  '172901',
  '119995',
  '131201',
  '162101',
  '173201',
  '191201',
  '181120',
  '144866',
  '191103',
  '191104',
]

const companyTralleStandardAllowedWasteIds = [
  '160303',
  '119932',
  '119939',
  '114904',
  '70980036',
  '161502',
  '113102',
  '161702',
  '149946',
  '122111',
  '159905',
  '172901',
  '119995',
  '131201',
  '162101',
  '173201',
  '191201',
  '181120',
  '144866',
  '191103',
  '191104',
  '161304',
  '160111',
  '160123',
  '160410',
  '160411',
  '160414',
  '160415',
  '161806',
  '160403',
  '161901',
]

const getWasteIdFromSku = (sku: string) => sku.split('-')[1] || ''
const getEquipmentIdFromSku = (sku: string) => sku.split('-')[0] || ''

const getLastPathSegment = (path: string) => {
  const segments = path.split('/').filter(Boolean)
  return segments[segments.length - 1] || path
}

const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, '').trim()

const matchesCompanyItem = (companyItem: any, sourceItem: any) => {
  const sourcePath = normalize(getLastPathSegment(sourceItem?.path || ''))
  const sourceName = normalize(sourceItem?.name || '')
  const sourceShortName = normalize(sourceItem?.shortName?.content?.text || '')
  const sourceVolume = normalize(sourceItem?.shortDescription?.content?.text || '')

  const companyPath = normalize(companyItem.path || '')
  const companyLabel = normalize(companyItem.label || '')
  const companyVolume = normalize(companyItem.volume || '')

  return [sourcePath, sourceName, sourceShortName, sourceVolume].some(
    (candidate) =>
      candidate === companyPath ||
      candidate.includes(companyPath) ||
      candidate.includes(companyLabel) ||
      candidate === companyVolume,
  )
}

const filterCompanyVariantsBySize = (path: string, variants: any[]) => {
  if (!['stor', 'xxl', 'tralle', 'standard'].includes(path)) return variants

  const wantedWasteIds = ['stor', 'xxl'].includes(path)
    ? companyStorXxlAllowedWasteIds
    : companyTralleStandardAllowedWasteIds

  const variantsByWasteId = new Map<string, any>()
  variants.forEach((variant: any) => {
    const wasteId = getWasteIdFromSku(variant?.sku || '')
    if (wasteId) variantsByWasteId.set(wasteId, variant)
  })

  const baseEquipmentId = getEquipmentIdFromSku(variants?.[0]?.sku || '')

  return wantedWasteIds.map((wasteId) => {
    const existingVariant = variantsByWasteId.get(wasteId)
    const mappedName = companyWasteTranslationKeyById[wasteId] || wasteId

    if (existingVariant) {
      return {
        ...existingVariant,
        name: mappedName,
      }
    }

    return {
      name: mappedName,
      sku: baseEquipmentId ? `${baseEquipmentId}-${wasteId}` : wasteId,
      priceVariants: [],
      components: [],
      firstImage: null,
      attributes: [],
    }
  })
}

export const ProductVariantSection = ({
  items,
  productType,
  handleSizeSelection,
  shouldShowProductVariants,
  isCompany = false,
  initialProductPath,
}: any) => {
  const t = useTranslations()
  const [productPath, setProductPath] = useState<any>()
  const [selectedSizePath, setSelectedSizePath] = useState<string>('')
  const [selectedItemKey, setSelectedItemKey] = useState<string>('')
  const [variants, setVariants] = useState<any>()

  const allSourceVariants = (items || []).flatMap((item: any) => item?.variants || [])
  const productItems = isCompany
    ? companyWasteBagItems.map((companyItem) => {
        const sourceItem = items?.find((item: any) => matchesCompanyItem(companyItem, item))
        const sourceVariants = sourceItem?.variants?.length ? sourceItem.variants : allSourceVariants
        const sourcePath = sourceItem?.path || items?.[0]?.path || companyItem.path

        return {
          ...companyItem,
          path: sourcePath,
          sizePath: companyItem.path,
          variants: filterCompanyVariantsBySize(companyItem.path, sourceVariants),
        }
      })
    : items
  const mobileColumns = productItems?.length === 4 ? 2 : 3

  const selectItem = (item: any) => {
    setVariants(item?.variants || [])
    setProductPath(item?.path || '')
    setSelectedSizePath(item?.sizePath || '')
    setSelectedItemKey(item?.sizePath || item?.path || '')
    handleSizeSelection()
  }

  const handleClick = (item: any, e: any) => {
    e.preventDefault()
    selectItem(item)
  }

  const matchesInitialProduct = (item: any) => {
    if (!initialProductPath) return false
    // Company items are keyed by size (tralle/standard/stor/xxl) rather than a full catalogue path
    if (item.sizePath) return initialProductPath.startsWith(item.sizePath)
    return getLastPathSegment(item?.path || '') === initialProductPath
  }

  useEffect(() => {
    if (!initialProductPath) return
    const match = productItems.find(matchesInitialProduct)
    if (match) selectItem(match)
    // Only run once on mount to preselect the product carried over via the `product` query param
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <p className="text-lg font-bold">{t('Choose_size')}</p>
      <div className="h-6" />
      <div data-testid="product-card-grid" className="flex flex-wrap items-start justify-start gap-[10px] sm:gap-5">
        {productItems.map((item: any) => (
          <ProductCard
            key={item.sizePath || item.path}
            handleClick={handleClick}
            item={item}
            mobileColumns={mobileColumns}
            isSelected={(item.sizePath || item.path) === selectedItemKey}
          />
        ))}
      </div>
      <div className="h-6" />
      {shouldShowProductVariants ? (
        <ProductVariantsList
          data-testid="product-variant-list"
          variants={variants || []}
          productPath={productPath || ''}
          sizePath={isCompany ? selectedSizePath : undefined}
          serviceType="PICKUP"
          productType={productType}
          isCompany={isCompany}
        />
      ) : null}
    </>
  )
}
